// eslint-disable-next-line import/no-extraneous-dependencies -- pino is a root workspace dependency
import pino from 'pino'
// eslint-disable-next-line import/no-extraneous-dependencies -- safe-stable-stringify is root workspace dependency (same lib Pino uses)
import stringify from 'safe-stable-stringify'
import { getDatadogLoggerBaseMeta } from '../utils'

type LogMeta = Record<string, unknown>

/** Normalise Error instances to a plain object so payload stays one-line when serialised (no util.inspect multi-line). */
function serializableError(err: Error): Record<string, unknown> {
  const ext = err as unknown as Record<string, unknown>
  return {
    message: err.message,
    stack: err.stack,
    name: err.name,
    ...(typeof ext.code !== 'undefined' && { code: ext.code }),
  }
}

function mergePayload(message: string, meta?: LogMeta | Error | unknown): LogMeta {
  const base = getDatadogLoggerBaseMeta()
  if (!meta) {
    return { ...base, message }
  }
  if (meta instanceof Error) {
    return {
      ...base,
      message,
      error: serializableError(meta),
    }
  }
  if (typeof meta === 'object' && meta !== null && !Array.isArray(meta)) {
    const spread = meta as LogMeta
    const error = spread.error instanceof Error ? serializableError(spread.error) : spread.error
    return { ...base, ...spread, error, message }
  }
  return { ...base, message, meta: String(meta) }
}

/**
 * Edge has no Node streams, so we use Pino's Browser API.
 * @see https://getpino.io/#/docs/browser — "write (Function | Object)"
 *
 * When `write` is an object, it can have methods per level; the corresponding
 * method is called for each log. We serialize the log object to one JSON line
 * and pass it to the matching console.* so aggregators see one event per line.
 *
 * We use safe-stable-stringify (same lib Pino uses for Node serialization) so
 * circular refs and BigInt don't throw; circular refs become "[Circular]".
 */
/** One writer per level so we never fall back to console (which would pretty-print). */
function createBrowserWrite() {
  /* eslint-disable no-console -- Edge has no stdout; console is the log sink per Pino Browser API */
  const out = (method: 'error' | 'warn' | 'info' | 'debug' | 'trace') => (o: object) => {
    const line = stringify(o)
    if (method === 'error') {
      console.error(line)
    } else if (method === 'warn') {
      console.warn(line)
    } else if (method === 'info') {
      console.log(line)
    } else {
      console.debug(line)
    }
  }
  return {
    fatal: out('error'),
    error: out('error'),
    warn: out('warn'),
    info: out('info'),
    debug: out('debug'),
    trace: out('trace'),
  }
  /* eslint-enable no-console */
}

const createEdgePino = () =>
  pino({
    base: undefined,
    formatters: {
      level: (label: string) => ({ level: label }),
    },
    browser: {
      write: createBrowserWrite(),
    },
  })

const pinoInstance = createEdgePino()

/**
 * Datadog uses the reserved "status" attribute for severity. We set it so logs
 * show as error/warn in the UI and Error Tracking without changing Datadog pipelines.
 */
const LOG_LEVEL_TO_STATUS: Record<string, string> = {
  error: 'error',
  fatal: 'error',
  warn: 'warn',
  info: 'info',
  debug: 'debug',
  trace: 'trace',
}

const log =
  (level: 'info' | 'warn' | 'error' | 'debug') =>
  (message: string, meta?: LogMeta | Error | unknown) => {
    const payload = {
      ...mergePayload(message, meta),
      status: LOG_LEVEL_TO_STATUS[level] ?? 'info',
    }
    pinoInstance[level](payload)
  }

const edgeLogger = {
  info: (message: string, meta?: LogMeta | Error | unknown) => log('info')(message, meta),
  warn: (message: string, meta?: LogMeta | Error | unknown) => log('warn')(message, meta),
  error: (message: string, meta?: LogMeta | Error | unknown) => log('error')(message, meta),
  debug: (message: string, meta?: LogMeta | Error | unknown) => log('debug')(message, meta),
  log: (
    level: 'info' | 'warn' | 'error' | 'debug',
    message: string,
    meta?: LogMeta | Error | unknown
  ) => log(level)(message, meta),
}

export default edgeLogger
